"use client";

import React from "react";
import { Loader, Send, ChevronDown } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GetInTouch() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="15312" id="start-your-project" ref={ref} className={`relative w-full py-24 bg-background text-foreground transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-6xl font-black text-foreground mb-4 font-sans tracking-tighter uppercase">
              Start Your Project
            </h2>
            <p className="text-muted-foreground text-lg font-sans max-w-lg mx-auto">
              Elevate your brand with Moondos Media. Let&apos;s define your digital identity together.
            </p>
          </div>

          <Card className="bg-background/40 backdrop-blur-xl border border-white/10 rounded-none shadow-2xl overflow-hidden">
            <CardHeader className="sr-only">
              <CardTitle>Project Inquiry Form</CardTitle>
            </CardHeader>
            <CardContent className="p-6 md:p-10">
              <FormProvider formId="get_in_touch_form_rootedinhopeservices" sectionName="get-in-touch" defaultValues={{
              name: "",
              email: "",
              phone: "",
              inquiry_type: "",
              message: ""
            }} validationRules={{
              name: {
                required: "Name is required"
              },
              email: {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Invalid email address"
                }
              },
              phone: {
                pattern: {
                  value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
                  message: "Invalid phone number"
                }
              },
              inquiry_type: {
                required: "Please select a reason"
              },
              message: {
                required: "Message is required"
              }
            }} submitText="LET'S BUILD SOMETHING GREAT" successMessage="Your project brief has been received. Our creative team will reach out shortly.">
                <ContactFormContent />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-none bg-secondary/20 flex items-center justify-center mb-4 border border-secondary">
          <Send className="size-8 text-secondary" />
        </div>
        <h3 className="text-3xl font-black text-foreground font-sans uppercase tracking-tighter">Brief Received</h3>
        <p className="text-muted-foreground max-w-md font-sans">{successMessage}</p>
        <Button variant="outline" className="mt-6 border-2 border-secondary text-secondary hover:bg-secondary hover:text-white transition-all duration-300 rounded-none font-mono" onClick={() => window.location.reload()}>
          Submit Another Brief
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid gap-8 md:grid-cols-2">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Full Name" className="font-sans bg-transparent border-0 border-b-2 border-white/10 focus-visible:border-secondary focus-visible:ring-0 rounded-none h-12 transition-all duration-500 placeholder:text-muted-foreground/30 focus-visible:shadow-[0_4px_20px_-5px_rgba(129,90,255,0.3)]" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] uppercase font-mono" />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@company.com" className="font-sans bg-transparent border-0 border-b-2 border-white/10 focus-visible:border-secondary focus-visible:ring-0 rounded-none h-12 transition-all duration-500 placeholder:text-muted-foreground/30 focus-visible:shadow-[0_4px_20px_-5px_rgba(129,90,255,0.3)]" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] uppercase font-mono" />
            </FormItem>} />
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Company / Organization</FormLabel>
              <FormControl>
                <Input type="text" placeholder="Your Agency or Brand" className="font-sans bg-transparent border-0 border-b-2 border-white/10 focus-visible:border-secondary focus-visible:ring-0 rounded-none h-12 transition-all duration-500 placeholder:text-muted-foreground/30 focus-visible:shadow-[0_4px_20px_-5px_rgba(129,90,255,0.3)]" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-mono text-muted-foreground/50 uppercase">
                Optional – help us understand your scale
              </FormDescription>
              <FormMessage className="text-[10px] uppercase font-mono" />
            </FormItem>} />

        <FormField control={control} name="inquiry_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Service Interest
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-12 w-full items-center justify-between rounded-none border-0 border-b-2 border-white/10 bg-transparent px-0 py-2 text-sm shadow-none ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:border-secondary focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 font-sans appearance-none transition-all duration-500" {...field} value={field.value || ""}>
                    <option value="" disabled className="bg-background">
                      Select Project Type
                    </option>
                    <option value="Website Development" className="bg-background">Website Development</option>
                    <option value="Social Media Management" className="bg-background">Social Media Management</option>
                    <option value="Branding" className="bg-background">Branding</option>
                    <option value="Personal Branding" className="bg-background">Personal Branding</option>
                    <option value="Content Creation" className="bg-background">Content Creation</option>
                  </select>
                  <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
                </div>
              </FormControl>
              <FormMessage className="text-[10px] uppercase font-mono" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Project Description</FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe your vision and goals..." className="font-sans bg-transparent border-0 border-b-2 border-white/10 focus-visible:border-secondary focus-visible:ring-0 rounded-none min-h-[100px] resize-none transition-all duration-500 placeholder:text-muted-foreground/30 focus-visible:shadow-[0_4px_20px_-5px_rgba(129,90,255,0.3)]" {...field} />
            </FormControl>
            <FormDescription className="text-[10px] font-mono text-muted-foreground/50 uppercase">
              Include any links or references that might help.
            </FormDescription>
            <FormMessage className="text-[10px] uppercase font-mono" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none">
          <p className="text-destructive text-xs font-mono uppercase">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-16 bg-secondary hover:bg-secondary/80 text-white font-black text-xl rounded-none shadow-2xl transition-all duration-500 font-sans tracking-widest uppercase">
        {isSubmitting ? <>
            <Loader className="mr-2 h-6 w-6 animate-spin" />
            INITIATING...
          </> : "LET'S BUILD SOMETHING GREAT"}
      </Button>
    </Form>;
}